import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "../Shift/constants";
import { formatBreakDuration } from "./formatBreakDuration";
import { formatMinutesDuration } from "./formatMinutesDuration";

interface FormatDurationWithBreakPartsParams {
  /**
   * Total shift duration in hours (including break)
   */
  totalDurationInHours: number;
  /**
   * Break duration in minutes
   */
  breakDurationInMinutes: number;
  /**
   * Whether the shift requires a break
   */
  requiresBreak: boolean;
  /**
   * Minutes elapsed since the start of the shift
   */
  minutesElapsed?: number;
  /**
   * "detailed" renders "8h" + "incl. 30min unpaid break".
   * "overview" renders "8 hr total" + "30 min unpaid".
   */
  variant?: "detailed" | "overview";
}
/**
 * Formats a shift duration and returns separate parts for duration and break info
 * @returns Object with separate duration and break info strings
 */
export function formatDurationWithBreakParts(props: FormatDurationWithBreakPartsParams): {
  duration: string;
  breakInfo: string;
} {
  const {
    totalDurationInHours,
    breakDurationInMinutes,
    requiresBreak,
    minutesElapsed = 0,
    variant = "detailed",
  } = props;

  const totalDurationInMinutes = Math.round(totalDurationInHours * 60 - minutesElapsed);

  const hasBreak =
    requiresBreak &&
    breakDurationInMinutes > 0 &&
    totalDurationInHours >= MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS;

  if (variant === "overview") {
    return {
      duration: `${formatMinutesDuration(totalDurationInMinutes)} total`,
      breakInfo: hasBreak ? `${formatMinutesDuration(breakDurationInMinutes)} unpaid` : "",
    };
  }

  const totalHours = Math.floor(totalDurationInMinutes / 60);
  const totalMinutes = totalDurationInMinutes % 60;

  let duration = `${totalHours}h`;
  if (totalMinutes > 0) {
    duration += ` ${totalMinutes.toString().padStart(2, "0")}m`;
  }

  let breakInfo = "";

  if (!hasBreak) {
    breakInfo = "no mandatory break";
  } else {
    const breakFormatted = formatBreakDuration(breakDurationInMinutes);
    breakInfo = `incl. ${breakFormatted} unpaid break`;
  }

  return { duration, breakInfo };
}
